"use client"

import clsx from "clsx"
import { Dialog } from "radix-ui"
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type FC,
  type KeyboardEventHandler,
} from "react"
import { defaults } from "../../defaults"
import { useBreakpoint } from "../../hooks/useBreakpoints"
import { useEscCloseStack } from "../../hooks/useEscCloseStack"
import { useLatestValue } from "../../hooks/useLatestValue"
import { useScrollable } from "../../hooks/useScrollable"
import { focusableElements, preventDefaultHandler, toCssVariables } from "../../lib/helpers"
import { useRadixPortalContainer } from "../PlatformUIKitProvider/internal"
import { TransitionGroup } from "../Transition"
import s from "./Modal.module.css"
import { ModalContext, useModalContext, type ModalContextValue } from "./useModalContext"
import { useModalClose } from "./useModalController"

export const MODAL_ENTER_DURATION = 600
export const MODAL_EXIT_DURATION = 300

export type ModalProps = {
  /** Sets controlled visibility state */
  open?: boolean
  /** The open state of the dialog when it is initially rendered. Use when you do not need to control its open state. */
  defaultOpen?: boolean
  /** Callback invoked when visibility state changes */
  onOpenChange?: (nextState: boolean) => void
  children: React.ReactNode
}

export const Modal = ({
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange: controlledOnOpenChange,
  children,
}: ModalProps) => {
  const [open, setOpen] = useState<boolean>(defaultOpen)
  const [shake, setShake] = useState<boolean>(false)
  const checkAllowClose = useRef(new Set<() => "deny" | "allow">())
  const isOpen = controlledOpen ?? open

  const latestOpenChange = useLatestValue(controlledOnOpenChange)

  const handleOpenChange = useCallback(
    (nextState: boolean) => {
      // No-op if the state is not changing
      if (isOpen === nextState) {
        return
      }

      if (!nextState) {
        // clear any running shake animation on close
        setShake(false)
      }

      latestOpenChange.current?.(nextState)

      // When visibility is managed, only call the user's onOpenChange handler
      if (typeof controlledOpen !== "boolean") {
        setOpen(nextState)
      }
    },
    [isOpen, controlledOpen, latestOpenChange],
  )

  const store = useMemo<ModalContextValue>(
    () => ({
      open: isOpen,
      setOpen: handleOpenChange,
      shake,
      setShake,
      checkAllowClose,
    }),
    [isOpen, handleOpenChange, shake, setShake],
  )

  return (
    <ModalContext value={store}>
      <Dialog.Root
        open={isOpen}
        onOpenChange={handleOpenChange}
        // IMPORTANT: Avoid `modal` from Radix, as it ruins compatibility with other layers.
        modal={false}
      >
        {children}
      </Dialog.Root>
    </ModalContext>
  )
}

type TriggerProps = {
  children: React.ReactNode
  ref?: React.Ref<HTMLButtonElement | null>
}

const Trigger = ({ children, ref }: TriggerProps) => {
  return (
    <Dialog.Trigger asChild ref={ref}>
      {children}
    </Dialog.Trigger>
  )
}

const isInteractionOutsideCloseExempt = (target: unknown) => {
  if (!(target instanceof Element)) {
    return false
  }

  const isGrammarly = target.hasAttribute("data-grammarly-shadow-root")
  const is1Password = target.tagName === "COM-1PASSWORD-BUTTON"
  const isSonnerToast = target.closest("[data-sonner-toast], [data-sonner-toaster]") != null
  const isPuikIgnoreOutside = target.closest("[data-puik-ignore-outside-interaction]") != null

  return isGrammarly || is1Password || isSonnerToast || isPuikIgnoreOutside
}

const Content = ({
  children,
  className,
  /**
   * Set the `max-width` of the popover, in pixels.
   * @default 450
   */
  maxWidth,
  /**
   * Determines how the Modal will scroll when content exceeds available vertical space.
   * @default inside
   */
  scroll = "inside",
  /**
   * Sets whether backdrop of the modal is dimmed (darker) or faded (lighter).
   * @default defaults.backdropStyle
   */
  backdrop = defaults.backdropStyle,
}: {
  children: React.ReactNode
  className?: string
  maxWidth?: number
  scroll?: "inside" | "outside"
  backdrop?: "dim" | "fade"
}) => {
  const isSmallScreen = !useBreakpoint("md")
  const { open, setOpen, shake, checkAllowClose } = useModalContext()
  // Small screen modals always scroll inside
  const derivedScroll = isSmallScreen ? "inside" : scroll
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null)
  const radixPortalContainer = useRadixPortalContainer()

  useEscCloseStack(open, () => {
    for (const fn of checkAllowClose.current) {
      if (fn() === "deny") {
        return
      }
    }
    setOpen(false)
  })

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (evt) => {
    if (
      // When the current target is our container
      contentElement &&
      evt.target === contentElement &&
      // We know that shift key tabs will lead to closing the modal (because we don't use `modal`)
      evt.key === "Tab" &&
      evt.shiftKey
    ) {
      // Prevent that from happening
      evt.preventDefault()
      evt.stopPropagation()

      // Move focus to the last focusable element, placing the user in the focus loop
      const focusable = focusableElements(contentElement)
      const lastFocusable = focusable[focusable.length - 1]
      lastFocusable?.focus()
    }
  }

  // Important that we send initial focus into the modal, placing it onto the container.
  // This allows focus looping to begin, as well as capturing scoped key presses.
  // We prevent Radix's default behavior of focusing an element, because this can have adverse side-effects.
  useEffect(() => {
    if (!contentElement) {
      return
    }

    // If we're already focused within the content area, no-op.
    // This would be true if some inner component declares autoFocus
    if (contentElement?.contains(document.activeElement)) {
      return
    }

    contentElement.focus({ preventScroll: true })
  }, [contentElement])

  const maybePreventDefaultCloseOnInteractOutside = useCallback(
    (e: Event) => {
      if (isInteractionOutsideCloseExempt(e.target)) {
        e.preventDefault()
        return
      }

      // Never let a focus event close a modal - this only ever creates problems for us, such was browser autocomplete.
      if (e.type === "dismissableLayer.focusOutside" || e.type === "dismissableLayer.blurOutside") {
        e.preventDefault()
        return
      }

      for (const fn of checkAllowClose.current) {
        if (fn() === "deny") {
          e.preventDefault()
          return
        }
      }
    },
    [checkAllowClose],
  )

  return (
    <Dialog.Portal forceMount container={radixPortalContainer}>
      <TransitionGroup
        enterDuration={MODAL_ENTER_DURATION}
        exitDuration={MODAL_EXIT_DURATION}
        className={s.ModalPortal}
      >
        {open && (
          // Necessary to use a single wrapper so that Radix ref can be forwarded
          <div key="modal" className={s.ModalPortalInner} data-backdrop={backdrop}>
            {/*
             * Note: The <Dialog.Overlay /> has odd requirements to enable scrolling. It offers no real value, so we use our own element.
             */}
            <div key="overlay" className={s.ModalBackdrop} />
            <div className={s.ModalContainer} data-modal-scroll={derivedScroll}>
              <div className={s.ModalContainerInner}>
                <Dialog.Content
                  forceMount
                  ref={setContentElement}
                  className={clsx(s.Modal, className)}
                  style={toCssVariables({
                    "modal-max-width": maxWidth,
                  })}
                  data-animate={shake ? "shake" : undefined}
                  onOpenAutoFocus={preventDefaultHandler}
                  onEscapeKeyDown={preventDefaultHandler}
                  onKeyDown={handleKeyDown}
                  onPointerDownOutside={maybePreventDefaultCloseOnInteractOutside}
                  onInteractOutside={maybePreventDefaultCloseOnInteractOutside}
                >
                  {children}
                </Dialog.Content>
              </div>
            </div>
          </div>
        )}
      </TransitionGroup>
    </Dialog.Portal>
  )
}

// Passes through the layout provided by Content, allowing Header/Body/Footer to be wrapped
const Form = ({ children, className, ...restProps }: React.FormHTMLAttributes<HTMLFormElement>) => {
  return (
    <form className={clsx(s.Form, className)} {...restProps}>
      {children}
    </form>
  )
}

const Header = ({ children }: { children: React.ReactNode }) => {
  return <div className={s.Header}>{children}</div>
}

const Body = ({ children, className, ...restProps }: React.HTMLAttributes<HTMLDivElement>) => {
  const elementRef = useRef<HTMLDivElement>(null)
  const { isScrollable, isAtStart } = useScrollable(elementRef)

  return (
    <div
      ref={elementRef}
      className={clsx(s.Body, className)}
      data-scrollable={isScrollable}
      data-scroll-at-top={isAtStart}
      {...restProps}
    >
      {children}
    </div>
  )
}

const Footer = ({ children, className, ...restProps }: ComponentProps<"div">) => {
  return (
    <div className={clsx(s.Footer, className)} {...restProps}>
      {children}
    </div>
  )
}

const Description = ({
  /** Hide the description visually, but keep included for screen readers. */
  visuallyHidden = false,
  /**
   * Content rendered for the description
   */
  children,
  className,
}: {
  visuallyHidden?: boolean
  children: React.ReactNode
  className?: string
}) => {
  return (
    <Dialog.Description
      className={clsx(s.Description, className)}
      data-visually-hidden={visuallyHidden ? "" : undefined}
    >
      {children}
    </Dialog.Description>
  )
}

const Title = ({
  children,
  className,
  ...restProps
}: {
  /**
   * Content rendered for the title
   */
  children: React.ReactNode
  className?: string
}) => {
  return (
    <Dialog.Title className={clsx(s.Title, className)} {...restProps}>
      {children}
    </Dialog.Title>
  )
}

/*
 * Supports both component and render function composition
 * ```
 *  <Modal.Close>
 *    {({ close }) => (
 *      <button onClick={close}>Close Modal</button>
 *    )}
 *  </Modal.Close>
 * ```
 */
const Close: FC<{
  children: React.ReactNode | ((props: { close: () => void }) => React.ReactNode)
}> = ({ children }) => {
  const close = useModalClose()

  if (typeof children === "function") {
    return <>{children({ close })}</>
  }

  return <Dialog.Close asChild>{children}</Dialog.Close>
}

Modal.Trigger = Trigger
Modal.Content = Content
Modal.Form = Form
Modal.Header = Header
Modal.Body = Body
Modal.Footer = Footer
Modal.Title = Title
Modal.Description = Description
Modal.Close = Close
